import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

/* NaN-safe radius guard — invalid geometry renders nothing instead of console errors */
const rr = (v) => (Number.isFinite(v) ? v : 0.1);

const LAYERS = [
    { name: "Outer Sheath", spec: "PVC ST2 / LSZH", desc: "UV-stable, rodent-resistant jacket rated -40°C to +90°C continuous service.", std: "IEC 60502-1", color: "#1B1E28" },
    { name: "Steel Wire Armour", spec: "Galvanised SWA", desc: "Mechanical protection and earth continuity for direct burial and ducted routes.", std: "IS 7098-1", color: "#232838" },
    { name: "Bedding", spec: "Extruded PP", desc: "Water-tight inner sheath that cushions and rounds the core assembly.", std: "—", color: "#1D212B" },
    { name: "XLPE Insulation", spec: "Cross-linked PE", desc: "90°C continuous / 250°C short-circuit rated dielectric wall.", std: "IEC 60502", color: "#2B3040" },
    { name: "Fillers & Binder", spec: "PP fillers, binder tape", desc: "Non-hygroscopic fillers round the laid-up cores and hold the cable geometry. At 1.1 kV there are no semiconducting screens — those begin at medium voltage.", std: "IS 7098-1", color: "#14171F" },
    { name: "Copper Conductor", spec: "99.99% ETP Cu, Class 2", desc: "Stranded annealed copper at 0.0172 Ω·mm²/m — the heart of every Roxy metre.", std: "IS 8130", color: "#B85C22" },
];

/* Realistic 4-core XLPE SWA construction: laid-up stranded cores, PP fillers,
   binder, extruded bedding, individual galvanised armour wires, outer sheath. */

const SHEATH = { outer: 225, inner: 206 };
const ARM_WIRE = { ring: 196, r: 10 };
const BEDDING = { outer: 186, inner: 140 };
const CORE = { dist: 82, insulation: 58, wall: 7 };
const STRAND = { r: 12, ring: 24 };

const coreCenters = [0, 90, 180, 270].map((deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: 240 + CORE.dist * Math.cos(rad), y: 240 + CORE.dist * Math.sin(rad), deg };
});
const CORE_COLORS = ["#7A3B1D", "#20242E", "#4A5568", "#1F618D"]; // brown, black, grey, blue
const armourWires = Array.from({ length: 54 }, (_, i) => {
    const rad = (i / 54) * 2 * Math.PI;
    return { x: 240 + ARM_WIRE.ring * Math.cos(rad), y: 240 + ARM_WIRE.ring * Math.sin(rad), key: i };
});
const strandOffsets = (cx, cy) =>
    [{ x: cx, y: cy }, ...[0, 60, 120, 180, 240, 300].map((deg) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return { x: cx + STRAND.ring * Math.cos(rad), y: cy + STRAND.ring * Math.sin(rad) };
    })];
/* PP fillers: centre cross-filler + four interstitial fillers between cores */
const fillerCenters = [{ x: 240, y: 240, r: 20 }, ...[45, 135, 225, 315].map((deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: 240 + 84 * Math.cos(rad), y: 240 + 84 * Math.sin(rad), r: 24 };
})];

const HIT_RINGS = [
    { mid: (SHEATH.outer + SHEATH.inner) / 2, w: SHEATH.outer - SHEATH.inner }, // 0 sheath
    { mid: ARM_WIRE.ring, w: ARM_WIRE.r * 2 },                                   // 1 armour
    { mid: (BEDDING.outer + BEDDING.inner) / 2, w: BEDDING.outer - BEDDING.inner }, // 2 bedding
];

export default function CrossSection() {
    const [active, setActive] = useState(5);
    const layer = LAYERS[active];

    return (
        <section id="anatomy" className="relative hud-bg py-20 lg:py-28 border-y border-line" data-testid="anatomy-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal className="relative order-2 lg:order-1">
                    <div className="relative rounded-md bg-inkdark p-4 md:p-8 shadow-[0_24px_60px_rgba(24,23,22,0.2)]">
                        <div className="relative mx-auto w-full max-w-[460px] aspect-square">
                            <svg viewBox="0 0 480 480" className="w-full h-full" data-testid="anatomy-diagram">
                                <defs>
                                    <radialGradient id="sheathGrad" cx="36%" cy="30%" r="85%">
                                        <stop offset="0%" stopColor="#3A4152" />
                                        <stop offset="45%" stopColor="#1B1E28" />
                                        <stop offset="100%" stopColor="#0B0D13" />
                                    </radialGradient>
                                    <radialGradient id="steelGrad" cx="35%" cy="30%" r="80%">
                                        <stop offset="0%" stopColor="#E8ECF2" />
                                        <stop offset="55%" stopColor="#9AA2B0" />
                                        <stop offset="100%" stopColor="#565E6E" />
                                    </radialGradient>
                                    <radialGradient id="cuStrand" cx="35%" cy="30%" r="80%">
                                        <stop offset="0%" stopColor="#F0B67F" />
                                        <stop offset="55%" stopColor="#C56B2E" />
                                        <stop offset="100%" stopColor="#7A3B12" />
                                    </radialGradient>
                                    <radialGradient id="bedGrad" cx="40%" cy="34%" r="80%">
                                        <stop offset="0%" stopColor="#2C3140" />
                                        <stop offset="100%" stopColor="#14171F" />
                                    </radialGradient>
                                    <radialGradient id="sheen" cx="34%" cy="28%" r="75%">
                                        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.10" />
                                        <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.02" />
                                        <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                                    </radialGradient>
                                </defs>

                                {/* outer sheath */}
                                <circle cx="240" cy="240" r={rr(SHEATH.outer)} fill="url(#sheathGrad)" stroke="#05070B" strokeWidth="2"
                                    opacity={active === 0 ? 1 : 0.85} className="transition-opacity duration-300" />
                                {/* sheath ribs */}
                                <circle cx="240" cy="240" r={rr(SHEATH.outer - 4)} fill="none" stroke="#FFFFFF" strokeOpacity="0.05" strokeWidth="2" strokeDasharray="2 7" />

                                {/* galvanised steel wire armour — individual wires */}
                                <g opacity={active === 1 ? 1 : 0.75} className="transition-opacity duration-300">
                                    {armourWires.map((w) => (
                                        <circle key={w.key} cx={w.x} cy={w.y} r={rr(ARM_WIRE.r)} fill="url(#steelGrad)" stroke="#2E3542" strokeWidth="1" />
                                    ))}
                                </g>

                                {/* extruded bedding */}
                                <circle cx="240" cy="240" r={rr(BEDDING.outer)} fill="url(#bedGrad)" stroke="#0E1118" strokeWidth="1.5"
                                    opacity={active === 2 ? 1 : 0.85} className="transition-opacity duration-300" />

                                {/* binder tape */}
                                <circle cx="240" cy="240" r={rr(BEDDING.inner - 1)} fill="none" stroke="#8B93A5" strokeOpacity="0.35" strokeWidth="5" strokeDasharray="14 4" />

                                {/* core assembly zone (fillers show in the gaps) */}
                                <circle cx="240" cy="240" r={rr(BEDDING.inner - 3)} fill="#10131B" />
                                <g opacity={active === 4 ? 1 : 0.8} className="transition-opacity duration-300">
                                    {fillerCenters.map((f, i) => (
                                        <circle key={i} cx={f.x} cy={f.y} r={rr(f.r)} fill="#262B37" stroke="#171B25" strokeWidth="1" />
                                    ))}
                                </g>

                                {/* four laid-up cores */}
                                {coreCenters.map((c, ci) => (
                                    <g key={ci}>
                                        <circle cx={c.x} cy={c.y} r={rr(CORE.insulation)} fill={CORE_COLORS[ci]} stroke="#0A0C11" strokeWidth="2"
                                            opacity={active === 3 ? 1 : 0.9} className="transition-opacity duration-300" />
                                        <circle cx={c.x - 14} cy={c.y - 16} r={rr(CORE.insulation - 18)} fill="#FFFFFF" opacity="0.06" />
                                        <circle cx={c.x} cy={c.y} r={rr(CORE.insulation - CORE.wall)} fill="#15100B" stroke="#3A2A18" strokeWidth="1" />
                                        <g opacity={active === 5 ? 1 : 0.92} className="transition-opacity duration-300">
                                            {strandOffsets(c.x, c.y).map((s, si) => (
                                                <g key={si}>
                                                    <circle cx={s.x} cy={s.y} r={rr(STRAND.r)} fill="url(#cuStrand)" stroke="#5C2E0E" strokeWidth="0.8" />
                                                    <circle cx={s.x} cy={s.y} r={rr(STRAND.r - 4.5)} fill="none" stroke="#8A4517" strokeWidth="0.7" opacity="0.7" />
                                                </g>
                                            ))}
                                        </g>
                                    </g>
                                ))}

                                {/* soft top-left sheen for roundness */}
                                <circle cx="240" cy="240" r={rr(SHEATH.outer)} fill="url(#sheen)" pointerEvents="none" />

                                {/* hit zones */}
                                {HIT_RINGS.map((h, i) => (
                                    <circle key={i} cx="240" cy="240" r={rr(h.mid)} fill="none" stroke="transparent" strokeWidth={h.w}
                                        className="cursor-pointer" style={{ pointerEvents: "stroke" }}
                                        onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} />
                                ))}
                                {coreCenters.map((c, ci) => (
                                    <g key={`hit-${ci}`}>
                                        <circle cx={c.x} cy={c.y} r={rr(CORE.insulation)} fill="transparent" className="cursor-pointer"
                                            onClick={() => setActive(3)} onMouseEnter={() => setActive(3)} />
                                        <circle cx={c.x} cy={c.y} r={rr(CORE.insulation - CORE.wall)} fill="transparent" className="cursor-pointer"
                                            onClick={() => setActive(5)} onMouseEnter={() => setActive(5)} />
                                    </g>
                                ))}
                                {fillerCenters.map((f, i) => (
                                    <circle key={`hf-${i}`} cx={f.x} cy={f.y} r={rr(f.r)} fill="transparent" className="cursor-pointer"
                                        onClick={() => setActive(4)} onMouseEnter={() => setActive(4)} />
                                ))}
                                {/* active ring indicator */}
                                {active < 3 && (
                                    <circle cx="240" cy="240" r={rr(HIT_RINGS[active].mid)} fill="none" stroke="#D97736" strokeOpacity="0.7"
                                        strokeWidth="2" strokeDasharray="6 6" pointerEvents="none" />
                                )}
                            </svg>
                            <span className="absolute top-2 right-2 font-mono text-[10px] tracking-[0.25em] text-white/40 uppercase">
                                FIG. 02 — 4C × 25 SQMM, 1.1 kV XLPE SWA
                            </span>
                        </div>
                    </div>
                </Reveal>

                <div className="order-1 lg:order-2">
                    <Reveal>
                        <Eyebrow className="mb-5">02 — Anatomy</Eyebrow>
                        <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink mb-4">
                            Read a cable <em className="text-copper">like a spec sheet.</em>
                        </h2>
                        <p className="text-mist leading-relaxed mb-8 max-w-md">
                            Hover or tap the layers. Every build ships with a mill test certificate and
                            full traceability — because a cable is a 25-year decision.
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="border border-line bg-white rounded-md overflow-hidden">
                            {LAYERS.map((l, i) => (
                                <button
                                    key={l.name}
                                    data-testid={`anatomy-layer-${i}`}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => setActive(i)}
                                    className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-line last:border-b-0 transition-colors duration-300 ${
                                        active === i ? "bg-copper-light border-l-2 border-l-copper" : "border-l-2 border-l-transparent hover:bg-paper2"
                                    }`}
                                >
                                    <span className="flex items-center gap-4">
                                        <span className={`font-mono text-[10px] ${active === i ? "text-copper" : "text-dim"}`}>
                                            0{i + 1}
                                        </span>
                                        <span className={`font-display font-semibold text-base ${active === i ? "text-copper" : "text-ink"}`}>
                                            {l.name}
                                        </span>
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.15em] text-dim uppercase hidden sm:block">
                                        {l.spec}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="mt-5 rounded-md border border-line bg-white p-6 shadow-sm"
                            data-testid="anatomy-panel"
                        >
                            <p className="font-mono text-[10px] tracking-[0.25em] text-copper uppercase mb-2">
                                Layer {active + 1} / 06 — {layer.std}
                            </p>
                            <h3 className="font-display font-semibold text-2xl text-ink mb-2">{layer.name}</h3>
                            <p className="text-mist text-sm leading-relaxed">{layer.desc}</p>
                        </motion.div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
